import { useMemo, useState } from 'react';
import {
  ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, TruckIcon, BuildingStorefrontIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  useResetTargets, useResetCounts, useResetPreview, useApplyReset, useResetHistory,
} from '../hooks/useStockReset';

// Stock reset — making a physical count the opening balance.
//
// A van and a storeroom get here by different routes, and the page says so
// rather than pretending they are the same:
//
//   A VAN's level is derived from its opening count plus receipts minus job
//   usage, so a reset moves no stock at all. It repoints the opening balance at
//   a later count and everything recomputes.
//
//   A STOREROOM's level is a running ledger, so a reset posts a correcting
//   movement per item. That is a real change, and it is shown line by line
//   first.
//
// Both leave usage since the count alone: it stays on the engineer's next
// order, which is the whole point of resetting rather than zeroing.

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB',
  { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const num = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

function CountCard({ count, chosen, isCurrent, onPick }) {
  return (
    <button
      type="button" onClick={onPick}
      className={`w-full rounded-lg border p-3 text-left transition ${
        chosen ? 'border-brand bg-brand/5 ring-1 ring-brand' : 'border-gray-200 bg-white hover:border-gray-300'}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-semibold text-gray-900">{fmtDate(count.count_date)}</span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
          week {count.count_week}
        </span>
        {count.off_cycle ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">off cycle</span>
        ) : null}
        {isCurrent ? (
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800">
            current opening balance
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-gray-600">
        {count.counted_by || 'unknown'} · {count.lines_n} lines · {num(count.units)} units
      </p>
    </button>
  );
}

function StockReset() {
  const [type, setType] = useState('van');
  const [target, setTarget] = useState('');
  const [countId, setCountId] = useState(null);
  const [reason, setReason] = useState('');
  const [zeroIds, setZeroIds] = useState(() => new Set());
  const [done, setDone] = useState(null);
  const [search, setSearch] = useState('');

  const { data: targets, isLoading: targetsLoading, isError: targetsError } = useResetTargets();
  const counts = useResetCounts(type, target);
  const preview = useResetPreview(type, target, countId);
  const apply = useApplyReset();
  const history = useResetHistory();

  const list = (type === 'van' ? targets?.vans : targets?.storerooms) || [];
  const chosenTarget = list.find((t) => String(t.target) === String(target)) || null;
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const scored = q
      ? list.filter((t) => `${t.target} ${t.driver || ''} ${t.name || ''}`.toLowerCase().includes(q))
      : list;
    // Recently counted first when nothing is typed: a reset almost always
    // follows a count somebody has just done.
    return [...scored].sort((a, b) => new Date(b.latest_count) - new Date(a.latest_count)).slice(0, 40);
  }, [list, search]);
  const currentIds = new Set(counts.data?.current?.baseline_count_ids || []);
  const p = preview.data;

  // The latest count is the usual answer, so it is offered first and marked.
  const ordered = useMemo(() => counts.data?.counts || [], [counts.data]);

  const reset = (nextType) => {
    setType(nextType); setTarget(''); setCountId(null); setReason('');
    setZeroIds(new Set()); setDone(null); setSearch('');
  };

  const toggleZero = (sid) => setZeroIds((prev) => {
    const next = new Set(prev);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    return next;
  });

  if (done) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
          <CheckCircleIcon className="mx-auto h-10 w-10 text-emerald-600" />
          <h1 className="mt-2 text-lg font-bold text-emerald-900">
            {done.type === 'van' ? `${done.target} reset` : 'Storeroom reset'}
          </h1>
          <p className="mt-1 text-sm text-emerald-800">
            {done.type === 'van'
              ? `Opening balance is now count #${done.count_id} (week ${done.baseline_week}). Nothing was moved — every figure recomputes from that count.`
              : `${done.adjustments} correcting movement(s) posted so the ledger matches count #${done.count_id}.`}
          </p>
          <p className="mt-2 text-sm text-emerald-800">
            Anything used since that count stays on the next order.
          </p>
          {done.skipped?.length ? (
            <p className="mt-2 text-xs text-amber-800">
              {done.skipped.length} item(s) skipped — no product record: {done.skipped.map((s) => s.item_code).join(', ')}
            </p>
          ) : null}
          <button
            type="button" onClick={() => reset(done.type)}
            className="mt-4 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white"
          >
            Reset something else
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-1 flex items-center gap-2">
        <ArrowPathIcon className="h-6 w-6 text-brand" />
        <h1 className="text-xl font-bold text-gray-900">Stock reset</h1>
      </div>
      <p className="mb-5 max-w-3xl text-sm text-gray-500">
        Make a physical count the opening balance. A van reset moves no stock — its level is derived, so
        every figure simply recomputes from the count you choose. A storeroom reset posts a correcting
        movement per item, because its level is a ledger. Either way, what has been used since the count
        stays on the next order.
      </p>

      {targetsError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          You need Inventory &amp; Operations access to reset stock.
        </p>
      ) : null}

      {/* 1. what is being reset */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-gray-300 p-0.5">
          {[['van', 'Van', TruckIcon], ['storeroom', 'Storeroom', BuildingStorefrontIcon]].map(([k, label, Icon]) => (
            <button
              key={k} type="button" onClick={() => reset(k)}
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium ${
                type === k ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* 196 vans in a plain dropdown is a scroll, and nobody knows the
            registration of the van they were told about by driver name — so it
            searches both. */}
        {chosenTarget ? (
          <div className="inline-flex items-center gap-2 rounded-md border border-brand bg-brand/5 px-3 py-2 text-sm">
            <span className="font-semibold text-gray-900">
              {type === 'van' ? chosenTarget.target : chosenTarget.name}
            </span>
            <span className="text-gray-500">
              {type === 'van' && chosenTarget.driver ? `${chosenTarget.driver} · ` : ''}
              {chosenTarget.counts} counts · last {fmtDate(chosenTarget.latest_count)}
            </span>
            <button
              type="button"
              onClick={() => { setTarget(''); setCountId(null); setZeroIds(new Set()); setSearch(''); }}
              className="ml-1 rounded border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-white"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative w-full max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={targetsLoading ? 'Loading…'
                : type === 'van' ? 'Search by registration or driver' : 'Search storerooms'}
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
            />
            {list.length ? (
              <div className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                {matches.length ? matches.map((t) => (
                  <button
                    key={String(t.target)} type="button"
                    onClick={() => { setTarget(String(t.target)); setCountId(null); setZeroIds(new Set()); }}
                    className="flex w-full items-baseline gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-gray-50"
                  >
                    <span className="font-semibold text-gray-900">
                      {type === 'van' ? t.target : t.name}
                    </span>
                    {type === 'van' && t.driver ? <span className="text-gray-600">{t.driver}</span> : null}
                    <span className="ml-auto whitespace-nowrap text-xs text-gray-400">
                      {t.counts} counts · last {fmtDate(t.latest_count)}
                    </span>
                  </button>
                )) : (
                  <p className="px-3 py-3 text-sm text-gray-400">
                    Nothing matches “{search}”. Only {type}s that have been counted can be reset.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* 2. which count */}
      {target ? (
        <div className="mb-5">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
            Counts on record — newest first
          </h2>
          {counts.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
          {!counts.isLoading && !ordered.length ? (
            <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-400">
              This {type} has never been counted, so there is nothing to reset to.
            </p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ordered.map((c) => (
              <CountCard
                key={c.id} count={c} chosen={countId === c.id} isCurrent={currentIds.has(c.id)}
                onPick={() => { setCountId(c.id); setZeroIds(new Set()); }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* 3. what it would do */}
      {countId && preview.isLoading ? <p className="text-sm text-gray-400">Working out the difference…</p> : null}
      {p ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
            <span><span className="font-semibold text-gray-900">{p.totals.counted_lines}</span> counted lines</span>
            <span><span className="font-semibold text-gray-900">{num(p.totals.counted_units)}</span> units counted</span>
            <span>
              <span className="font-semibold text-gray-900">{p.totals.moves}</span>{' '}
              {type === 'van' ? 'lines change figure' : 'corrections to post'}
            </span>
            {type === 'storeroom' ? (
              <span><span className="font-semibold text-gray-900">{p.totals.uncounted_lines}</span> holding stock but not counted</span>
            ) : null}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Item</th>
                  <th className="px-4 py-2 text-right font-medium">Counted</th>
                  <th className="px-4 py-2 text-right font-medium">System says</th>
                  <th className="px-4 py-2 text-right font-medium">Change</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {p.items.filter((i) => i.delta !== 0 || i.counted !== 0).map((i) => (
                  <tr key={i.stock_item_id} className={i.delta !== 0 ? 'bg-amber-50/40' : ''}>
                    <td className="px-4 py-1.5 text-gray-800">
                      {i.name}
                      <span className="ml-2 font-mono text-xs text-gray-400">{i.item_code}</span>
                    </td>
                    <td className="px-4 py-1.5 text-right tabular-nums text-gray-900">{num(i.counted)}</td>
                    <td className="px-4 py-1.5 text-right tabular-nums text-gray-500">{num(i.current)}</td>
                    <td className={`px-4 py-1.5 text-right tabular-nums font-semibold ${
                      i.delta > 0 ? 'text-emerald-700' : i.delta < 0 ? 'text-rose-700' : 'text-gray-400'}`}>
                      {i.delta > 0 ? `+${num(i.delta)}` : num(i.delta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* holding stock, nobody counted it */}
          {p.uncounted.length ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <h3 className="text-sm font-bold text-amber-900">
                Holding stock but not on this count ({p.uncounted.length})
              </h3>
              <p className="mb-2 text-xs text-amber-800">
                Left alone unless you tick them: a missing line usually means nobody counted it, not that
                there is none. Ticking one zeroes it as part of the reset.
              </p>
              <div className="max-h-64 space-y-0.5 overflow-y-auto">
                {p.uncounted.map((u) => (
                  <label key={u.stock_item_id} className="flex items-center gap-2 text-sm text-amber-900">
                    <input
                      type="checkbox" checked={zeroIds.has(u.stock_item_id)}
                      onChange={() => toggleZero(u.stock_item_id)}
                      className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="flex-1">{u.name} <span className="font-mono text-xs text-amber-700">{u.item_code}</span></span>
                    <span className="tabular-nums font-semibold">{num(u.current)}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {/* what stays owed */}
          {p.usage_since?.length ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
              <h3 className="text-sm font-bold text-sky-900">
                Used since this count — stays on the next order ({p.usage_since.length})
              </h3>
              <p className="mb-2 text-xs text-sky-800">
                The reset does not forgive these. Each item is measured from the later of this count and the
                last time it was actually received, so what has been used and not replaced still gets ordered.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-sky-900">
                {p.usage_since.map((u) => (
                  <span key={u.stock_item_id}>{u.name} <span className="font-semibold">{num(u.used)}</span></span>
                ))}
              </div>
            </div>
          ) : null}

          {/* commit */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <label className="block text-sm font-medium text-gray-700">Why is this being reset?</label>
            <input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. van changed hands, or books drifted after a bad pick"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Kept against the reset with your name, and shown wherever the opening balance is questioned.
            </p>

            {apply.isError ? (
              <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {apply.error?.response?.data?.error || 'Could not apply that reset.'}
              </p>
            ) : null}

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                disabled={!reason.trim() || apply.isPending}
                onClick={() => apply.mutate(
                  { type, target, count_id: countId, reason: reason.trim(), zero_ids: [...zeroIds] },
                  { onSuccess: (d) => setDone({ ...d, type, target }) })}
                className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {apply.isPending ? 'Resetting…'
                  : type === 'van' ? 'Use this count as the opening balance'
                    : `Post ${p.totals.moves + zeroIds.size} correction(s)`}
              </button>
              {type === 'storeroom' ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  This changes real balances.
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* 4. what has been reset before */}
      {history.data?.length ? (
        <div className="mt-8">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Recent resets</h2>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">What</th>
                  <th className="px-4 py-2 font-medium">Count</th>
                  <th className="px-4 py-2 font-medium">By</th>
                  <th className="px-4 py-2 font-medium">Why</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.data.map((h) => (
                  <tr key={`${h.type}:${h.target}:${h.count_id}:${h.set_at}`}>
                    <td className="whitespace-nowrap px-4 py-1.5 text-gray-600">{fmtDate(h.set_at)}</td>
                    <td className="px-4 py-1.5 text-gray-800">
                      {h.type === 'van' ? h.target : h.name || `storeroom ${h.target}`}
                      <span className="ml-2 text-xs text-gray-400">{h.type}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-1.5 text-gray-600">
                      #{h.count_id}{h.count_date ? ` · ${fmtDate(h.count_date)}` : ''}
                      {h.adjustments ? ` · ${h.adjustments} corrections` : ''}
                    </td>
                    <td className="whitespace-nowrap px-4 py-1.5 text-gray-600">{h.set_by_name || '—'}</td>
                    <td className="px-4 py-1.5 text-gray-500">{h.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default StockReset;
